/**
 * USB terazi okuma — Web Serial API (Chrome / Edge, HTTPS veya localhost).
 * Ayarlar backend ScaleController ile uyumlu: Shinko Denshi AJH 3200CE (9600 8N2).
 */

const BAUD_RATE = 9600;
const READ_TIMEOUT_MS = 3000;

export function isWebSerialSupported() {
  return typeof navigator !== 'undefined' && !!navigator.serial;
}

/** "+0013.27 G S" formatı — sunucudaki ParseScaleData ile aynı mantık */
export function parseScaleData(data) {
  const reading = { weight: null, unit: 'G', stable: false };
  if (!data || typeof data !== 'string') return reading;
  const match = data.match(/([+-]?\d+\.?\d*)\s*([A-Za-z]+)\s*([A-Za-z]*)/);
  if (!match) return reading;
  const w = parseFloat(match[1]);
  if (!Number.isNaN(w)) reading.weight = w;
  let unitAndStatus = (match[2] + ' ' + (match[3] || '')).trim();
  reading.stable = unitAndStatus.indexOf('S') >= 0;
  reading.unit = unitAndStatus.replace(/S/g, '').trim() || 'G';
  return reading;
}

function readWithTimeout(reader, ms) {
  if (ms <= 0) {
    return Promise.resolve({ value: undefined, done: true, timedOut: true });
  }
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve({ value: undefined, done: false, timedOut: true }), ms);
    reader
      .read()
      .then((chunk) => {
        clearTimeout(t);
        resolve(chunk);
      })
      .catch((err) => {
        clearTimeout(t);
        resolve({ value: undefined, done: true, error: err });
      });
  });
}

/**
 * @param {{ requestPortIfNeeded?: boolean }} opts — true olmalı (doğrudan kullanıcı tıklamasından çağrıda)
 */
export async function readScaleWeight(opts) {
  const requestPortIfNeeded = !opts || opts.requestPortIfNeeded !== false;

  if (!isWebSerialSupported()) {
    return {
      success: false,
      message:
        'Tarayıcınız USB teraziyi desteklemiyor. Chrome veya Edge kullanın; site HTTPS veya localhost olmalıdır.'
    };
  }

  let port;
  try {
    const ports = await navigator.serial.getPorts();
    if (ports.length > 0) {
      port = ports[0];
    } else if (requestPortIfNeeded) {
      port = await navigator.serial.requestPort();
    } else {
      return {
        success: false,
        code: 'NO_PORT',
        message: 'Terazi portu seçilmedi. "Tartımı Oku" düğmesine basın.'
      };
    }
  } catch (e) {
    if (e && e.name === 'NotFoundError') {
      return { success: false, message: 'Port seçimi iptal edildi.' };
    }
    return { success: false, message: (e && e.message) || String(e) };
  }

  async function openPort() {
    await port.open({
      baudRate: BAUD_RATE,
      dataBits: 8,
      parity: 'none',
      stopBits: 2
    });
  }

  try {
    await openPort();
  } catch (e) {
    const msg = (e && e.message) || String(e);
    if (msg.indexOf('already open') >= 0 || (e && e.name === 'InvalidStateError')) {
      try {
        await port.close();
      } catch (_) {
        /* ignore */
      }
      try {
        await openPort();
      } catch (e2) {
        return { success: false, message: 'Seri port açılamadı: ' + ((e2 && e2.message) || e2) };
      }
    } else {
      return { success: false, message: 'Seri port açılamadı: ' + msg };
    }
  }

  const reader = port.readable.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let lastStableLine = null;
  const startTime = Date.now();

  try {
    while (Date.now() - startTime < READ_TIMEOUT_MS) {
      const remaining = READ_TIMEOUT_MS - (Date.now() - startTime);
      const chunk = await readWithTimeout(reader, remaining);
      if (chunk.timedOut) break;
      if (chunk.error) break;
      if (chunk.done) break;
      if (chunk.value && chunk.value.length) {
        buffer += decoder.decode(chunk.value, { stream: true });
        const parts = buffer.split(/\r?\n/);
        buffer = parts.pop() || '';
        let gotStable = false;
        for (let i = 0; i < parts.length; i++) {
          const trimmed = parts[i].trim();
          if (!trimmed) continue;
          lastStableLine = trimmed;
          if (trimmed.indexOf('S') >= 0) {
            gotStable = true;
            break;
          }
        }
        if (gotStable) break;
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch (_) {
      /* ignore */
    }
    try {
      await port.close();
    } catch (_) {
      /* ignore */
    }
  }

  if (!lastStableLine) {
    return {
      success: false,
      message: 'Teraziden veri okunamadı. Terazinin açık olduğundan ve kablonun takılı olduğundan emin olun.'
    };
  }

  const parsed = parseScaleData(lastStableLine);
  if (parsed.weight == null || Number.isNaN(parsed.weight)) {
    return {
      success: false,
      raw: lastStableLine,
      message: 'Terazi satırı çözümlenemedi.'
    };
  }

  return {
    success: true,
    raw: lastStableLine,
    weight: parsed.weight,
    unit: parsed.unit,
    stable: parsed.stable
  };
}
